/**
 * Professional Trading Dashboard
 * Enterprise-grade interface with clean design and intuitive UX
 */

import { useState, useEffect } from 'react'
import { useAuthStore } from '@/stores/useAuthStore'
import { useTradingStore } from '@/stores/useTradingStore'
import { useMarketStore } from '@/stores/useMarketStore'
import { useCurrencyStore } from '@/stores/useCurrencyStore'
import { useWebSocket } from '@/hooks/useWebSocket'
import { handleError, handleSuccess } from '@/lib/errorHandler'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Footer } from '@/components/Footer'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { InstrumentList } from '@/components/InstrumentList'
import { PositionsList } from '@/components/PositionsList'
import { OrderEntryModal } from '@/components/OrderEntryModal'
import { WhatsAppWidget } from '@/components/WhatsAppWidget'
import { AIPanel } from '@/components/AIPanel'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatCurrency } from '@/lib/utils'
import { 
  LogOut, 
  RefreshCw, 
  Settings,
  History,
  TrendingUp,
  TrendingDown,
  Bot,
  Activity,
  Target,
  Wallet,
  PieChart,
  DollarSign
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import type { Instrument, Position } from '@/types'

export function DashboardPage() {
  const navigate = useNavigate()
  const { settings, logout, updateSettings } = useAuthStore()
  const { balance, positions, loadBalance, loadPositions, closePosition } = useTradingStore()
  const { loadInstruments } = useMarketStore()
  // Subscribe to currency changes - this makes the component re-render when currency changes
  const currency = useCurrencyStore((state) => state.currency)

  // Initialize WebSocket for real-time updates
  useWebSocket({
    autoConnect: true,
    onConnect: () => {
      console.log('Real-time updates connected')
      // Don't show toast on connect - too noisy
    },
    onDisconnect: () => {
      console.log('Real-time updates disconnected')
    },
    onError: (error) => {
      console.error('WebSocket error:', error)
      // Only show error if it's critical
    },
  })

  const [selectedInstrument, setSelectedInstrument] = useState<Instrument | undefined>()
  const [orderModalOpen, setOrderModalOpen] = useState(false)
  const [positionToClose, setPositionToClose] = useState<Position | undefined>()
  const [closeDialogOpen, setCloseDialogOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  useEffect(() => {
    // Initial load only - WebSocket will handle updates
    loadBalance()
    loadPositions()
    loadInstruments()
  }, [loadBalance, loadPositions, loadInstruments])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleEnvironmentChange = async (env: string) => {
    try {
      await updateSettings({ environment: env as 'live' | 'demo' })
      handleSuccess(`Switched to ${env.toUpperCase()} account`)
      // Refresh data for the new environment
      loadBalance()
      loadPositions()
      loadInstruments()
    } catch (error) {
      handleError(error)
    }
  }

  const handleSelectInstrument = (instrument: Instrument) => {
    setSelectedInstrument(instrument)
    setOrderModalOpen(true)
  }

  const handleClosePosition = (position: Position) => {
    setPositionToClose(position)
    setCloseDialogOpen(true)
  }

  const confirmClosePosition = async () => {
    if (!positionToClose) return

    setIsClosing(true)
    try {
      const result = await closePosition(positionToClose.id)
      if (result.success) {
        handleSuccess(`Closed ${positionToClose.instrument} position`, 'Position Closed')
        setCloseDialogOpen(false)
        setPositionToClose(undefined)
      } else {
        handleError(result.reason || 'Unable to close position', 'Close Position Failed')
      }
    } catch (error: any) {
      handleError(error, 'Close Position Failed')
    } finally {
      setIsClosing(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Professional Header - Mobile Optimized */}
      <motion.header 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 shadow-lg"
      >
        <div className="flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4 lg:px-6">
          {/* Left: Logo and Title */}
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="flex items-center gap-3 sm:gap-4 flex-shrink-0 cursor-pointer"
          >
            <div className="relative flex-shrink-0 group">
              <motion.div 
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary via-primary to-primary/80 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg transition-all duration-300 group-hover:shadow-[0_0_20px_rgba(var(--primary),0.5)] group-hover:scale-105"
              >
                <Activity className="h-4 w-4 sm:h-6 sm:w-6 text-primary-foreground" />
              </motion.div>
              <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-primary/0 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-primary rounded-full border-2 border-background">
                <div className="absolute inset-0 bg-primary rounded-full animate-ping opacity-75" />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-3">
                <h1 className="text-sm sm:text-lg lg:text-xl font-bold text-foreground">
                  Trading Platform
                </h1>
                {settings && (
                  <div className="hidden sm:block">
                    <Select value={settings.environment} onValueChange={handleEnvironmentChange}>
                      <SelectTrigger className="h-6 sm:h-7 text-xs py-0 px-2.5 bg-primary/10 border-primary/20 text-primary hover:bg-primary/20 transition-colors w-[90px] font-medium rounded-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="demo">DEMO</SelectItem>
                        <SelectItem value="live">LIVE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                {settings && (
                  <div className="sm:hidden">
                    <Select value={settings.environment} onValueChange={handleEnvironmentChange}>
                      <SelectTrigger className="h-6 text-[10px] py-0 px-2 bg-primary/10 border-primary/20 text-primary hover:bg-primary/20 transition-colors w-[80px] font-medium rounded-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="demo">DEMO</SelectItem>
                        <SelectItem value="live">LIVE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors px-1.5 py-0 whitespace-nowrap">
                    {currency === 'USD' ? (
                      <DollarSign className="h-3 w-3 mr-0.5" />
                    ) : (
                      <Wallet className="h-3 w-3 mr-0.5" />
                    )}
                    <span className="font-semibold text-[10px] sm:text-xs">{currency}</span>
                  </Badge>
                  <div className="flex items-center gap-1 text-[10px] sm:text-xs text-primary whitespace-nowrap">
                    <div className="relative w-1.5 h-1.5 sm:w-2 sm:h-2">
                      <div className="absolute inset-0 bg-primary rounded-full" />
                      <div className="absolute inset-0 bg-primary rounded-full animate-ping" />
                    </div>
                    <span>Connected</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Right: Actions */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Account Balance - Responsive */}
            {balance && (
              <div className="hidden md:flex items-center gap-2 lg:gap-3 px-2 lg:px-3 py-1.5 bg-card rounded-lg border border-border mr-1 lg:mr-2 hover:border-primary/30 transition-all duration-300 group flex-shrink-0">
                <div className="flex items-center gap-1.5 lg:gap-2">
                  <Wallet className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                  <div className="text-right">
                    <div className="text-[10px] lg:text-xs text-muted-foreground whitespace-nowrap">Balance</div>
                    <div className="text-xs lg:text-sm font-bold text-foreground whitespace-nowrap">
                      {formatCurrency(balance.balance)}
                    </div>
                  </div>
                </div>
                <div className="w-px h-6 lg:h-8 bg-border flex-shrink-0" />
                <div className="text-right">
                  <div className="text-[10px] lg:text-xs text-muted-foreground whitespace-nowrap">P&L</div>
                  <div className={`text-xs lg:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${balance.profitLoss >= 0 ? 'text-profit' : 'text-loss'}`}>
                    {balance.profitLoss >= 0 ? '+' : ''}{formatCurrency(balance.profitLoss)}
                  </div>
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/whatsapp')}
              className="relative hover:bg-primary/10 h-8 w-8 sm:h-9 sm:w-9 lg:h-10 lg:w-10 group transition-all duration-300 flex-shrink-0"
              title="AI Settings"
            >
              <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-primary group-hover:scale-110 transition-transform" />
              <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full">
                <div className="absolute inset-0 bg-primary rounded-full animate-ping" />
              </div>
              <div className="absolute inset-0 rounded-lg bg-primary/0 group-hover:bg-primary/5 transition-colors" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/history')}
              className="hidden sm:flex hover:bg-accent/10 h-8 w-8 sm:h-9 sm:w-9 lg:h-10 lg:w-10 group transition-all duration-300 flex-shrink-0"
              title="History"
            >
              <History className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5 group-hover:rotate-12 transition-transform" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                loadBalance()
                loadPositions()
                loadInstruments()
                handleSuccess('Market data updated')
              }}
              className="hidden sm:flex hover:bg-accent/10 h-8 w-8 sm:h-9 sm:w-9 lg:h-10 lg:w-10 group transition-all duration-300 flex-shrink-0"
              title="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5 group-hover:rotate-180 transition-transform duration-500" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings')}
              className="hover:bg-accent/10 h-8 w-8 sm:h-9 sm:w-9 lg:h-10 lg:w-10 group transition-all duration-300 flex-shrink-0"
              title="Settings"
            >
              <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5 group-hover:rotate-90 transition-transform duration-300" />
            </Button>
            
            <div className="w-px h-4 sm:h-5 lg:h-6 bg-border mx-0.5 sm:mx-1 flex-shrink-0" />
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="hover:bg-destructive/10 text-destructive h-8 w-8 sm:h-9 sm:w-9 lg:h-10 lg:w-10 group transition-all duration-300 flex-shrink-0"
              title="Logout"
            >
              <LogOut className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="container flex-1 py-6 px-4 lg:px-6 space-y-6 flex flex-col min-h-0">
        {/* Account Overview - Compact Mobile Grid */}
        {balance && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-6">
            {/* Total Balance */}
            <Card className="relative overflow-hidden bg-card border-border shadow-sm hover:shadow-md transition-all duration-300 group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <CardContent className="p-2.5 sm:p-4 lg:p-6 relative">
                <div className="flex items-start gap-2 mb-1.5 sm:mb-3">
                  <div className="w-7 h-7 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Wallet className="h-3.5 w-3.5 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-primary-foreground" />
                  </div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Balance</div>
                  <div className="text-sm sm:text-lg lg:text-2xl font-bold text-foreground truncate">
                    {formatCurrency(balance.balance)}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Available Balance */}
            <Card className="relative overflow-hidden bg-card border-border shadow-sm hover:shadow-md transition-all duration-300 group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <CardContent className="p-2.5 sm:p-4 lg:p-6 relative">
                <div className="flex items-start gap-2 mb-1.5 sm:mb-3">
                  <div className="w-7 h-7 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Target className="h-3.5 w-3.5 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-primary-foreground" />
                  </div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Available</div>
                  <div className="text-sm sm:text-lg lg:text-2xl font-bold text-foreground truncate">
                    {formatCurrency(balance.available)}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* P&L Card - Green for profit, red for loss */}
            <Card className={`relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group ${
              balance.profitLoss >= 0 
                ? 'bg-card border-profit' 
                : 'bg-card border-loss'
            }`}>
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                balance.profitLoss >= 0 
                  ? 'bg-gradient-to-br from-profit/5 to-transparent' 
                  : 'bg-gradient-to-br from-loss/5 to-transparent'
              }`} />
              <CardContent className="p-2.5 sm:p-4 lg:p-6 relative">
                <div className="flex items-start gap-2 mb-1.5 sm:mb-3">
                  <div className={`w-7 h-7 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 group-hover:scale-110 transition-transform duration-300 ${
                    balance.profitLoss >= 0 
                      ? 'bg-gradient-to-br from-profit to-profit/80' 
                      : 'bg-gradient-to-br from-loss to-loss/80'
                  }`}>
                    {balance.profitLoss >= 0 ? (
                      <TrendingUp className="h-3.5 w-3.5 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-profit-foreground animate-float" />
                    ) : (
                      <TrendingDown className="h-3.5 w-3.5 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-loss-foreground" />
                    )}
                  </div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Today P&L</div>
                  <div className={`text-sm sm:text-lg lg:text-2xl font-bold truncate ${
                    balance.profitLoss >= 0 ? 'text-profit' : 'text-loss'
                  }`}>
                    {balance.profitLoss >= 0 ? '+' : ''}{formatCurrency(balance.profitLoss)}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Positions Count */}
            <Card className="relative overflow-hidden bg-card border-border shadow-sm hover:shadow-md transition-all duration-300 group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <CardContent className="p-2.5 sm:p-4 lg:p-6 relative">
                <div className="flex items-start gap-2 mb-1.5 sm:mb-3">
                  <div className="w-7 h-7 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <PieChart className="h-3.5 w-3.5 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-primary-foreground group-hover:rotate-12 transition-transform" />
                  </div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Positions</div>
                  <div className="text-sm sm:text-lg lg:text-2xl font-bold text-foreground">
                    {positions.length}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Trading Interface */}
        <div className="grid gap-6 lg:grid-cols-12 items-start flex-1 min-h-0">
          {/* Left Column: Positions & AI Config */}
          <div className="flex flex-col gap-6 lg:col-span-4 h-[700px] lg:h-[calc(100vh-280px)] overflow-y-auto pr-1 lg:pr-2 pb-6 custom-scrollbar">
            <PositionsList onClosePosition={handleClosePosition} />
            <AIPanel />
          </div>

          {/* Center Column: Markets */}
          <div className="flex flex-col gap-6 lg:col-span-4 h-[700px] lg:h-[calc(100vh-280px)] overflow-y-auto pr-1 lg:pr-2 pb-6 custom-scrollbar">
            <InstrumentList onSelectInstrument={handleSelectInstrument} />
          </div>
          
          {/* Right Column: WhatsApp AI */}
          <div className="flex flex-col lg:col-span-4 h-[500px] lg:h-[calc(100vh-280px)] lg:overflow-hidden pb-6">
            <WhatsAppWidget />
          </div>
        </div>
      </main>

      <Footer />

      {/* Order Entry Modal */}
      <OrderEntryModal
        open={orderModalOpen}
        onClose={() => {
          setOrderModalOpen(false)
          setSelectedInstrument(undefined)
        }}
        instrument={selectedInstrument}
      />

      {/* Close Position Confirmation Dialog */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Position</DialogTitle>
            <DialogDescription>
              Are you sure you want to close this position?
            </DialogDescription>
          </DialogHeader>
          {positionToClose && (
            <div className="py-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Instrument:</span>
                  <span className="font-medium">{positionToClose.instrument}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Direction:</span>
                  <span className="font-medium">{positionToClose.direction}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Size:</span>
                  <span className="font-medium">{positionToClose.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current P&L:</span>
                  <span className={`font-medium ${(positionToClose.unrealizedPnL || 0) >= 0 ? 'text-profit' : 'text-loss'}`}>
                    {formatCurrency(positionToClose.unrealizedPnL || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCloseDialogOpen(false)}
              disabled={isClosing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmClosePosition}
              disabled={isClosing}
            >
              {isClosing ? 'Closing...' : 'Close Position'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
